// The factory, summarised as numbers a search can reason about.
//
// `scoreMove` weighs a move; this weighs a *board*. The distinction matters
// because the two halves of this game are not the same shape: chess is a
// position you can evaluate square by square, and a factory is a *flow* — the
// same eight units of iron are worth a knight beside a foundry and worth
// nothing at all in a queen's hands on the far side of the map.
//
// Pricing material by *where it is* is what makes hauling emerge from the
// state rather than from a rule per piece. Before it, held material scored the
// same wherever it sat, so a queen who had swept a file and collected eight
// units had no reason ever to walk them home — which is exactly how it played.

import {
  BISHOP,
  type Color,
  fileOf,
  KING,
  KNIGHT,
  PAWN,
  pieceColor,
  pieceType,
  QUEEN,
  rankOf,
  ROOK,
  type PieceType,
  type Side,
  forwardFacing,
  square
} from "src/engine/rules/board";
import {
  DESIGN,
  MINTABLE,
  mintCost,
  PIECE_VALUE,
  RULES
} from "src/engine/rules/rules";
import {
  billFor,
  METAL,
  mintGradeOf,
  FUEL,
  SPECIAL,
  STONE,
  topMetal
} from "src/engine/rules/materials";
import type { GameState } from "src/engine/rules/commands";
import { areEnemies, facingAt, pieceAt } from "src/engine/rules/position";
import {
  beltOwnerAt,
  bufferTotal,
  cookingCount,
  cookingUnitAt,
  depositAt,
  heapAt,
  mineableAt,
  mineableSquares,
  depositYieldAt,
  unitAt,
  unitCount,
  unitGrade,
  unitLine,
  unitMeets
} from "src/engine/rules/industry/state";
import {
  burnsAsFuel as burnsAsCoal,
  hopsToForge,
  isFoundry,
  harvestRate,
  isHarvesterAt,
  refinesAt,
  machineType
} from "src/engine/rules/industry/machines";
import { coalitionPaysBill, fundingOptions } from "src/engine/rules/industry/coalition";
import { laneReaches } from "./lanes";
import { WEIGHTS, type Weights } from "./tuning";


/** What a unit of material is worth, by grade. Refining is the value-add. */
/** Kept as an export because half the AI imports it; the table is the source. */
export const GRADE_VALUE = WEIGHTS.grade;

/**
 * What a unit is worth to the evaluation — the tuner's per-line copy of
 * `RULES.materialValue`. `w.grade` is the metal ladder; stone and the
 * specials have tables of their own.
 */
export function unitWorth(unit: number, w: Weights = WEIGHTS): number {
  const grade = unitGrade(unit);
  if (grade > 4) return 0;
  switch (unitLine(unit)) {
    case STONE:
      return w.gradeStone[grade] ?? 0;
    case SPECIAL:
      return w.gradeSpecial[grade] ?? 0;
    // Fuel (§31 M2) has no weights of its own: coal is priced as iron — the
    // raw unit it lets an oven raise — and nuclear as crucible.
    case FUEL:
      return (grade === 0 ? w.grade[0] : w.grade[2]) ?? 0;
    default:
      return w.grade[grade] ?? 0;
  }
}

/**
 * How much of a unit's value survives the distance to a machine that could use
 * it: `1 / (1 + k·d)`.
 *
 * Material in transit is not worthless — it is on its way — but it is not
 * doing anything yet either, and the difference between those two is the whole
 * reason a piece carrying something walks it somewhere.
 *
 * A hyperbola rather than a line with a floor, and the difference is the point
 * of the term. A floor is flat, and it is flat *exactly where hauling matters
 * most*: a queen nine squares out and one eight squares out both sat on the
 * floor, so the first step home was worth nothing and she never took it.
 * Measured — she moved one square in ten turns. This never stops decreasing,
 * so there is always a gradient to walk down, and it never reaches zero, so
 * carrying a load is never worse than dropping it.
 */

/** What clearing the economy's worst bottleneck is worth — about two thirds of a knight. */

/**
 * Squares that could take material off this side's hands: any machine with an
 * inbox, and any warehouse.
 *
 * Deliberately *not* asked per unit. This prices where material is, not
 * whether a particular oven would accept a particular bar — `acceptsUnit` is
 * the authority on that and asking it per unit per square per evaluation is a
 * different order of cost for an answer that moves a piece one square.
 */
export function consumerSquares(state: GameState, side: Side): number[] {
  const out: number[] = [];
  for (const [sq, piece] of state.position.board) {
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    const kind = machineType(state.position, sq);
    if (refinesAt(state.position, sq) || isFoundry(kind)) out.push(sq);
  }
  for (const [sq, structure] of state.industry.structures) {
    if (!areEnemies(state.position, side, structure.owner)) out.push(sq);
  }
  return out;
}

/** How much of its value a unit keeps, sitting this far from a machine. */
export function reachFactor(
  sq: number,
  consumers: readonly number[],
  w: Weights = WEIGHTS
): number {
  let best = Infinity;
  for (const at of consumers) {
    const distance = Math.max(
      Math.abs(fileOf(at) - fileOf(sq)),
      Math.abs(rankOf(at) - rankOf(sq))
    );
    if (distance < best) best = distance;
  }
  // Nowhere to take it at all is the same as being very far from anywhere.
  if (best === Infinity) return 1 / (1 + w.reachFalloff * w.farEnough);
  return 1 / (1 + w.reachFalloff * best);
}

/** Stands in for "no machine anywhere", so the answer stays finite and small. */

export interface FactoryView {
  /**
   * Material **in transit** — riding a lane, or committed to an oven — priced
   * by how near it is to being used.
   *
   * What is banked in a piece's hands used to be counted here too. The two are
   * separated now (`banked`) because they are different states of the same
   * metal and a tuner could not tell them apart: a lane carrying eight units is
   * a factory working, and a foundry sitting on eight units is a decision not
   * being made, and both scored identically.
   */
  material: number;
  /**
   * Material **in your pieces' hands**, spendable now. Same pricing, same
   * distance discount — only the weight is its own (`w.banked`).
   */
  banked: number;
  /**
   * What this side could put on the board **this turn**, discounted.
   *
   * Deliberately not the same question as `capacity` (is the plant wide
   * enough) or `inFlight` (is metal on its way). This is: is there a foundry
   * holding enough of the right grade, right now, to forge something — and how
   * many different things could it choose between.
   */
  readiness: number;
  /** What the plant is worth against the work waiting at each stage. */
  capacity: number;
  /**
   * Whether the stages are in the right **order** — ovens between the ore and
   * the forge, rather than a forge upstream of the oven feeding it.
   *
   * `capacity` counts machines and `throughput` finds the narrowest one; both
   * are blind to layout. See `orderValue`.
   */
  order: number;
  /**
   * What the chain can actually put through per turn, end to end.
   *
   * The number `capacity` only gestures at. A factory is a *pipeline*, and a
   * pipeline is worth its narrowest point: six diggers and one oven is a
   * one-oven factory, and the five spare diggers are not five sixths of an
   * improvement, they are nothing. Taking the minimum across the stages is
   * what makes "what should I build next" have an answer — it is always
   * whichever stage the minimum came from.
   */
  throughput: number;
  /**
   * What the *widest* stage could put through, against `throughput`'s narrowest.
   *
   * **The other half of the pipeline reading, and the head's `choke` input is
   * the gap between the two.** That input used to be measured against
   * `capacity`, which is not a plant's width at all: `capacityValue` scores
   * stages this side has **no** machine for, so it is identically zero for any
   * army holding a pawn, a king and a rook — measured over six hundred boards
   * across eleven armies, `capacity > 0` on 0% of them for nine profiles, and
   * on **100%** for `classic`, the army with no factory at all. So the slot
   * meant to say "plant on paper against plant in fact" fired only for armies
   * with no plant, and never for the ones with one. Every promoted champion had
   * that input column searched against a constant.
   *
   * Same units and the same scaling as `throughput`, or the ratio is not a
   * share. Zero when nothing is moving, which the fill site reads as *no
   * choke* rather than as total choke: an empty pipeline is not a strangled
   * one.
   */
  widest: number;
  /**
   * Material actually on its way to a forge, weighted by value and by how
   * close it is to arriving.
   *
   * Distinct from `material`, which counts everything anywhere: this counts
   * only what is *moving toward the end of the chain*, which is the thing a
   * factory exists to produce. A warehouse full of diamond scores well on
   * material and nothing here, and that is the right reading — banked metal is
   * not production.
   */
  inFlight: number;
  /** Parts drawn and not yet paid for — the bill outstanding. */
  drawn: number;
  /**
   * How much of that bill this side can actually meet, as a ratio in [0, 1].
   *
   * Iron it can reach against what the outstanding run costs, and **1 when
   * nothing is drawn** — nothing owed is fully afforded. A ratio rather than a
   * count because it is the thing a *decision* turns on: half a lane's worth
   * of iron is the same situation whether the lane is two parts or twenty, and
   * a raw shortfall would read as ten times worse on the longer one.
   *
   * Priced as the shortfall it implies (`drawn * (1 - affordability)`, in
   * parts) rather than paid for directly, or a side would be paid for drawing
   * nothing — which is the trap `mintAnticipation` and `readiness` both had.
   */
  affordability: number;
  /**
   * Parts that will actually be **paid for on the next tick**: the per-turn
   * fund rate.
   *
   * A blueprint with a supplier touching it is a network arriving; one with
   * none is litter that looks like a plan. Derived from the board rather than
   * measured over time, because `evaluate` is asked about a *board* — it is
   * what `fundBlueprints` will do, asked one tick early, with each supplier's
   * iron budgeted so two parts cannot both spend the same unit.
   */
  funding: number;
}

/**
 * The whole summary for one side.
 *
 * Two numbers rather than a dozen, because what a search needs is something it
 * can compare — and because each of these is a *bottleneck* reading rather
 * than a tally. A stockpile with nothing to process it and a plant with
 * nothing to process are the same failure seen from opposite ends, and both
 * come out low here.
 */
export function viewFactory(state: GameState, side: Color, w: Weights = WEIGHTS): FactoryView {
  const consumers = consumerSquares(state, side);

  let material = 0;
  let banked = 0;
  let harvesters = 0;
  let refineries = 0;
  let foundries = 0;
  let raw = 0;
  let refined = 0;

  for (const [sq, piece] of state.position.board) {
    if (areEnemies(state.position, side, pieceColor(piece))) continue;

    const kind = machineType(state.position, sq);
    if (isHarvesterAt(state.position, sq)) harvesters++;
    if (refinesAt(state.position, sq)) refineries++;
    if (isFoundry(kind)) foundries++;

    const near = reachFactor(sq, consumers, w);
    for (const store of ["in", "out"] as const) {
      for (let i = 0; i < unitCount(state.industry, sq, store); i++) {
        const unit = unitAt(state.industry, sq, i, store);
        // Banked, not in transit: this is material a piece is *holding*, which
        // is the half of the economy that can be spent this turn.
        banked += unitWorth(unit, w) * near * w.banked;
        if (unitGrade(unit) === 0) raw++;
        else refined++;
      }
    }

    // And the oven. Material mid-cook is material, and it is *already* where
    // it needs to be — no discount, because it is being used right now.
    for (let i = 0; i < cookingCount(state.industry, sq); i++) {
      const unit = cookingUnitAt(state.industry, sq, i);
      material += unitWorth(unit, w);
      if (unitGrade(unit) === 0) raw++;
      else refined++;
    }
  }

  // Cargo on a lane, which the old summary did not count at all — so a loaded
  // network read as an empty one, and a turn spent filling a lane looked like
  // a turn spent doing nothing.
  for (const [sq, units] of state.industry.beltCargo) {
    const near = reachFactor(sq, consumers, w);
    for (const unit of units) {
      material += unitWorth(unit, w) * near;
      if (unitGrade(unit) === 0) raw++;
      else refined++;
    }
  }

  for (const [sq, structure] of state.industry.structures) {
    if (areEnemies(state.position, side, structure.owner)) continue;
    const near = reachFactor(sq, consumers, w);
    for (const unit of structure.stock) {
      material += unitWorth(unit, w) * near;
    }
  }

  let scrap = 0;
  for (const sq of mineableSquares(state.industry)) scrap += mineableAt(state.industry, sq);

  const forges = consumerSquares(state, side).filter((sq) =>
    isFoundry(machineType(state.position, sq))
  );

  const track = viewTrack(state, side);
  const pipeline = pipelineOf(state, side, harvesters, refineries, foundries, scrap);

  return {
    material,
    banked,
    readiness: readinessValue(state, side, w),
    capacity: capacityValue(scrap, raw, refined, harvesters, refineries, foundries, w),
    order: orderValue(state, side, scrap + raw, w),
    throughput: pipeline.narrowest,
    widest: pipeline.widest,
    inFlight: inFlightValue(state, side, forges, w),
    ...track
  };
}

/**
 * The bill for the track this side has drawn, and how much of it is being met.
 *
 * **A part costs iron now, so a run is a debt.** Nothing in the summary could
 * see one: `beltHeld` pays for track that exists, `deadTrack` charges for
 * track that earns nothing, and a *blueprint* is neither — it is a promise,
 * and the opponent was making them faster than its factory could keep them.
 * Measured headless before this: 24 parts drawn against 5 built.
 *
 * Two readings, because they answer different questions. `affordability` is
 * whether the promise can be kept *at all* — the stock of iron against the
 * whole outstanding run. `funding` is whether it is being kept *this turn* —
 * how many parts a supplier is actually standing next to. A side can be rich
 * and building nothing (its ore is nowhere near the run it drew), or nearly
 * broke and building steadily (one miner walking a lane out square by square),
 * and those are opposite situations that one number would give the same score.
 */
function viewTrack(
  _state: GameState,
  _side: Color
): { drawn: number; affordability: number; funding: number } {
  // **No debt to read** (§31 S2): a part is paid the moment it is laid, so
  // nothing is ever drawn and owed. Kept as the constant it now is so the
  // summary's shape — and every weight reading it — stays put.
  return { drawn: 0, affordability: 1, funding: 0 };
}

/**
 * How well this side's ovens sit **between the ore and the forge**.
 *
 * `capacity` says whether the plant is wide enough and `throughput` says how
 * narrow its narrowest stage is. Neither asks whether the stages are in the
 * right *order* — so an army could own a mine, an oven and a forge, score a
 * complete chain, and have the forge standing upstream of the oven with the
 * silver having to come back past the pit to reach it.
 *
 * **The reducer's own network answers it, so there is only one definition of
 * downstream** (`hopsToForge`). Asked of top-grade metal there is no refinery
 * in the question — nothing can raise a diamond — so what is left is exactly
 * this side's forges, and the hops it counts *are* how far the oven's output
 * has to travel. Searched forward from each oven and stopped at the first
 * forge, rather than as a whole field: the question is only ever asked of the
 * two or three machines that are ovens, and this runs per evaluated board.
 *
 * **An oven is never its own downstream**, so a king does not count: it
 * refines and forges, which is a whole line on one square. Counting it made
 * every king report a perfect layout, turning the term into a flat payment for
 * owning one — measured, enough to make the opponent prefer shuffling a rook
 * next to its king to forging with it.
 */
function orderValue(
  state: GameState,
  side: Color,
  /** Material at the head of the line: scrap in the ground and raw iron. */
  waiting: number,
  w: Weights = WEIGHTS
): number {
  // **Worth what there is work for**, exactly as `capacityValue` is. Whether
  // the stages are in the right order is a question about a journey from the
  // mine, so with nothing at the mine end it is not a question — and a term
  // that paid out anyway would be a flat bonus for owning an oven near a
  // forge, which is `capacity`'s job and already done. Measured: without this
  // the opponent preferred shuffling a rook next to its king to forging with
  // it, on a board with no ore on it at all.
  const work = Math.min(1, waiting / ORDER_SCALE);
  if (work <= 0) return 0;

  // **Ovens that are only ovens.** A king refines *and* forges, so it is a
  // whole line standing on one square and the question does not apply to it —
  // and if it were asked, the king would be its own downstream and every king
  // would report a perfect layout. Cheaper too: one search for the side rather
  // than one per machine that is both.
  const ovens: number[] = [];
  for (const [sq, piece] of state.position.board) {
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    const type = machineType(state.position, sq);
    if (refinesAt(state.position, sq) && !isFoundry(type)) ovens.push(sq);
  }
  if (ovens.length === 0) return 0;

  let total = 0;
  for (const oven of ovens) {
    const away = hopsToForge(state.position, state.industry, side, oven, w.chaseLimit);
    if (away < 0) continue;
    total += (w.order * work) / (1 + w.reachFalloff * away);
  }
  return total;
}

/**
 * The whole summary as one number, for the judge.
 *
 * One place that knows which fields make up a factory's worth. It was written
 * out at both call sites in `evaluate` — ours and theirs — and a field added to
 * the view scored for one side and not the other until somebody noticed, which
 * is a bias rather than a bug and therefore the kind that survives.
 */
/**
 * The summary as one number.
 *
 * **It takes the weights now, and the track terms are why.** Everything above
 * is priced inside `viewFactory` and arrives here already in centipawns;
 * `drawn`, `affordability` and `funding` are deliberately *facts* — the
 * planner reads the ratio to decide whether to draw more track, and a
 * pre-multiplied number is no use to it. So the one that is scored is priced
 * here, at the site that is asking what a board is worth.
 *
 * **Only one of them is scored, and that was measured rather than chosen.** A
 * charge for the shortfall — the parts a side has drawn and cannot pay for —
 * is the obvious other half and it is *wrong here*, at any size: the forge
 * candidate leaves a run unfunded a turn longer than the move candidate does,
 * so the charge lands on forging specifically and argues with
 * `forgeAppetite`, which prices a whole mint in single figures. Measured at
 * 4 a part, at 1, and at 0.5: the opponent stops building pieces at all.
 *
 * It is the same argument this file already makes about lanes carrying
 * pieces. `evaluate` is only ever asked about a board the tick has happened
 * on, and an unfunded part is a *prediction* — the board already states the
 * fact, since a blueprint is not a belt and `beltHeld` never paid for one, so
 * there is no over-payment to correct. Where the prediction belongs is one
 * decision earlier, in `wantedLanes`, which reads `affordability` and simply
 * stops drawing (`w.beltDebt`).
 */
export const factoryValue = (view: FactoryView, w: Weights = WEIGHTS): number =>
  view.material +
  view.banked +
  view.readiness +
  view.capacity +
  view.order +
  view.throughput +
  view.inFlight +
  // The rate the side is actually keeping its promises at. A blueprint with a
  // supplier standing beside it is a network arriving; one with none is
  // litter that looks like a plan — and *that* is a fact about the board
  // rather than a guess about the future, which is why this half is scored
  // and the shortfall is not.
  view.funding * w.beltFundRate;

/**
 * The ranks the planner forges: `MINTABLE` less the king (§31 M4). A king is an
 * ordinary bill to the rules, and the weights price none (`w.piece[KING]` is
 * nought, its surplus nothing), so a second royal is a choice the planner is
 * blind to — left to the player, not mispriced.
 */
export const PLANNER_MINTS: readonly PieceType[] = MINTABLE.filter((type) => type !== KING);

/**
 * What minting `type` would *gain*, over and above the metal it eats.
 *
 * The piece, less what it cost at the grade the recipe is priced in. This is
 * the same quantity `mintAnticipation` credits a fraction of, and it is the
 * only honest measure of whether forging improves the board: a recipe whose
 * surplus is negative is one the evaluation should refuse, and it will.
 */
export function mintSurplus(type: PieceType, w: Weights = WEIGHTS): number {
  return Math.max(
    0,
    w.piece[type] - mintCost(type) * (w.grade[mintGradeOf(type)] ?? 0)
  );
}

/**
 * Whether this side could forge something this turn, and how much choice it has.
 *
 * **The hoarding trap is the whole difficulty here.** Paying for readiness at
 * face value pays a side to stay ready — hold the metal, keep the credit, never
 * spend it — which is exactly the failure `mintAnticipation` was corrected for
 * when it credited two fifths of a whole piece and made forging a *loss*. So
 * this credits a fraction (`w.readiness`, well under one) of the surplus the
 * best affordable recipe would realise, leaving the rest to be collected by
 * actually putting the piece down. Being ready is worth having, and always
 * worth less than acting on it.
 *
 * The **best** affordable recipe rather than the sum of all of them, because a
 * foundry forges once and a king does one job a turn — a side that can afford
 * four things can still only do one. Breadth is credited separately and flatly
 * (`w.readinessBreadth`): a choice between a knight and an oven is worth
 * something, and it is not worth another piece.
 *
 * Counted per *type*, deduplicated across foundries. Two rooks that can each
 * afford a pawn is not two choices, and `throughput` is where parallel plant
 * is already priced.
 */
function readinessValue(state: GameState, side: Color, w: Weights = WEIGHTS): number {
  // **The real test a forge would apply**, and since the bill is split between
  // every foundry touching the landing square (PLAN-UI §28.4) that test is "is
  // there a square beside it where a forge it leads could be paid for" rather
  // than "does it hold the recipe itself". A foundry beside a rich neighbour
  // is ready, which is the point of the mechanic and is what the player sees.
  //
  // **Swept per foundry rather than per recipe**, which is why the loops are
  // this way round: who could chip in for a square is a fact about the board
  // and only the bill changes between recipes. This is inside `evaluate`, and
  // repeating the sweep five times over was measured at a third of the
  // planner's whole turn on a grown board.
  // Hoisted: loop-invariant, and this sits inside `evaluate`.
  const bills = PLANNER_MINTS.map((type) => billFor(type));
  const ready = PLANNER_MINTS.map(() => false);
  let found = 0;

  for (const [sq, piece] of state.position.board) {
    if (found === PLANNER_MINTS.length) break;
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    if (!isFoundry(machineType(state.position, sq))) continue;
    const options = fundingOptions(state.position, state.industry, sq, side);
    if (options.length === 0) continue;
    for (let i = 0; i < PLANNER_MINTS.length; i++) {
      if (ready[i]) continue;
      const payable = options.some((option) =>
        coalitionPaysBill(state.industry, option.funders, bills[i], option.reserve)
      );
      if (!payable) continue;
      ready[i] = true;
      found++;
    }
  }

  let best = 0;
  let breadth = 0;
  for (let i = 0; i < PLANNER_MINTS.length; i++) {
    if (!ready[i]) continue;
    breadth++;
    best = Math.max(best, mintSurplus(PLANNER_MINTS[i], w));
  }

  if (breadth === 0) return 0;
  return best * w.readiness + (breadth - 1) * w.readinessBreadth;
}

/**
 * What the chain can put through in a turn — its narrowest stage and its widest.
 *
 * Each stage is capacity **and** supply — an oven with nothing to cook puts
 * nothing through however many ovens there are, and so does a pile of scrap
 * with nobody standing on it. Multiplying the two would make one missing stage
 * merely expensive; taking the minimum makes it fatal, which is what a
 * pipeline actually is.
 *
 * **The widest is returned beside it because one number cannot say whether a
 * plant is choked.** A throughput of one is a stalled six-digger plant and a
 * healthy one-digger plant, and those want opposite decisions. The pair is the
 * whole of "plant on paper against plant in fact", and both are scaled the same
 * way so the ratio between them is unitless.
 */
function pipelineOf(
  state: GameState,
  side: Color,
  harvesters: number,
  refineries: number,
  foundries: number,
  scrap: number
): { narrowest: number; widest: number } {
  // Extraction: what is being dug *now*, not what could be if somebody walked
  // somewhere. A harvester standing on nothing is not a stage of anything.
  let digging = 0;
  for (const [sq, piece] of state.position.board) {
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    if (!isHarvesterAt(state.position, sq)) continue;
    // A tile of a field is **drilled** at `depositDigRate` whatever the piece's
    // own rate is, so the narrowest stage of a plant standing on ore is that
    // and not a miner's two. One line, because this asks what is being dug now
    // and therefore already knows which square.
    if (depositAt(state.industry, sq) > 0) digging += RULES.depositDigRate;
    else if (heapAt(state.industry, sq) > 0) digging += harvestRate(state.position, sq);
  }

  // A stage with no machines is a stopped chain, and a stage with machines and
  // nothing arriving is a stalled one. Both come out as zero, and the caller
  // wants to know which — `capacity` is the term that says.
  const cooking = Math.min(refineries * RULES.refineryRate, digging + scrapReachable(scrap));
  const forging = Math.min(foundries * RULES.refineryRate, cooking);
  return {
    narrowest: Math.min(digging, cooking, forging) * THROUGHPUT_VALUE,
    widest: Math.max(digging, cooking, forging) * THROUGHPUT_VALUE
  };
}

/** Scrap counts toward supply only in so far as anything could get at it. */
const scrapReachable = (scrap: number): number => Math.min(scrap, 1);

/**
 * What a unit per turn of end-to-end production is worth.
 *
 * High, and deliberately: a factory that turns one unit a turn into pieces is
 * worth more than the handful of ingots sitting in it, because it keeps doing
 * it. This is the number that makes "build the missing stage" beat "carry this
 * load", which is the whole point of measuring throughput rather than stock.
 */
export const THROUGHPUT_VALUE = 90;

/** Material on its way to a forge, by value and by how near it is to arriving. */
function inFlightValue(
  state: GameState,
  side: Color,
  forges: readonly number[],
  w: Weights = WEIGHTS
): number {
  if (forges.length === 0) return 0;

  let total = 0;
  const count = (sq: number, unit: number): void => {
    total += unitWorth(unit, w) * reachFactor(sq, forges, w);
  };

  // On a lane — and only really *in flight* if the lane arrives somewhere.
  //
  // **This term used to be the reward for stranding your own output.** Every
  // unit on any part of ours counted, discounted by straight-line distance to a
  // forge and by nothing else, so ore parked at the head of a spur that ended
  // in open ground two squares from a foundry scored almost as well as ore
  // about to arrive at one. It cannot get there — `advanceBelts` leaves cargo
  // on a terminal part rather than spilling it, which is the right rule and
  // means the material is simply out of the economy until somebody finishes or
  // re-points the lane.
  //
  // Discounted rather than dropped: it is nearer a forge than unmined scrap
  // is, and zeroing it would make finishing a lane the only way to score a haul
  // that is already half done.
  for (const [sq, units] of state.industry.beltCargo) {
    if (beltOwnerAt(state.industry, sq) !== side) continue;
    const moving = laneReaches(state, sq, side, w);
    for (const unit of units) {
      total +=
        unitWorth(unit, w) *
        reachFactor(sq, forges, w) *
        (moving ? 1 : w.strandedFlow);
    }
  }
  // And in a machine's hands on the way there — a refinery's finished output
  // is in flight in every sense that matters; it is done and it is going on.
  for (const [sq, piece] of state.position.board) {
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    for (let i = 0; i < unitCount(state.industry, sq, "out"); i++) {
      count(sq, unitAt(state.industry, sq, i, "out"));
    }
  }
  // **A foundry's own stock is not in flight — it has arrived.**
  //
  // This used to add it, undiscounted, on the reasoning that metal at the end
  // of the chain is closest to becoming a piece. True, and exactly backwards
  // for the failure that matters: it paid a *premium* for the material most
  // likely to be sitting still. Measured, ten Gold in a foundry's hopper was
  // worth 380 as material plus another 133 here, against a rook worth 500 —
  // so a foundry that spent its metal made the board worse and the opponent
  // correctly refused to build. It also contradicted this term's own rule,
  // which is that banked metal is not production.
  //
  // What replaces it is `readiness`, which prices the same material for the
  // same reason — it can be spent *now* — but is bounded below the surplus
  // that spending it would realise, so it cannot pay for hoarding.
  return total * w.inFlight;
}

/**
 * How much extra a unit is worth for being on its way to a forge rather than
 * merely existing.
 *
 * A fraction, not a multiple: this is a *second* reading of material that
 * `material` has already counted, and double counting it at full weight would
 * make moving a unit toward a foundry worth more than the unit.
 */

/**
 * What this side's *ability to produce* is worth, against what it has to work
 * with.
 *
 * A knight and a bishop cost the same and are worth the same in a trade, and
 * they are not remotely the same purchase when there are forty units of iron
 * on the floor and no oven.
 *
 * Priced as a **bottleneck**: each stage of the chain is worth having in
 * proportion to the material waiting at it and nothing at all beyond that, so
 * a second oven with nothing to cook is worth nothing and the first oven over
 * a pile of scrap is worth a great deal. That is what stops it becoming a flat
 * "build more machines" bonus — a thing an opponent would follow until it had
 * no army left.
 */
/**
 * Ore enough for the layout to matter. The same shape as `capacityValue`'s
 * per-stage divisors, and the same reasoning: one unit is a trickle, a few is
 * a line running.
 */
const ORDER_SCALE = 4;

function capacityValue(
  scrap: number,
  raw: number,
  refined: number,
  harvesters: number,
  refineries: number,
  foundries: number,
  w: Weights = WEIGHTS
): number {
  const wants = (work: number, machines: number, per: number): number =>
    Math.max(0, Math.min(work / per, 1) - Math.min(machines, 1));

  return (
    wants(scrap, harvesters, 6) * w.capacity +
    wants(raw, refineries, 4) * w.capacity +
    wants(refined, foundries, 6) * w.capacity
  );
}

/**
 * How hard this side is being leaned on, from 0 (nobody within reach) to 1
 * (an army of equal weight standing on top of it).
 *
 * The posture the whole turn is planned in. Two armies at opposite ends of a
 * board that keeps growing are not in a fight yet, and an opponent that opens
 * every game by marching is answering a question nobody asked — it should be
 * digging, because the war will still be there in fifteen turns and the ore
 * will not have mined itself. Equally, an army with a rook three squares away
 * has no business laying track.
 *
 * Each enemy piece counts for what it is worth, discounted by how far it has
 * to come; the sum is measured against what this side is worth, so being
 * outnumbered reads as pressure and outnumbering does not. It is a *ratio*
 * rather than a count, which is what makes it work on a board of any size and
 * with any number of armies on it.
 */
export function pressure(state: GameState, side: Color, w: Weights = WEIGHTS): number {
  let mine = 0;
  const ours: number[] = [];
  for (const [sq, piece] of state.position.board) {
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    mine += PIECE_WEIGHT[pieceType(piece)] ?? 0;
    ours.push(sq);
  }
  if (mine === 0 || ours.length === 0) return 1;

  let threat = 0;
  for (const [sq, piece] of state.position.board) {
    if (!areEnemies(state.position, side, pieceColor(piece))) continue;
    let closest = Infinity;
    for (const at of ours) {
      const distance = Math.max(
        Math.abs(fileOf(at) - fileOf(sq)),
        Math.abs(rankOf(at) - rankOf(sq))
      );
      if (distance < closest) closest = distance;
    }
    // A piece that has to walk ten squares is a tenth of the problem it would
    // be standing next to you. Hyperbolic for the same reason `reachFactor` is
    // — it never flattens, so "they are getting closer" is always felt.
    threat += (PIECE_WEIGHT[pieceType(piece)] ?? 0) / (1 + w.threatFalloff * closest);
  }

  return Math.min(1, threat / mine);
}

/** How much of a threat each piece is, which is close enough to its value. */
const PIECE_WEIGHT: Record<number, number> = {
  [PAWN]: 1,
  [KNIGHT]: 3,
  [BISHOP]: 3,
  [ROOK]: 5,
  [QUEEN]: 9,
  [KING]: 3
};

/**
 * How fast a distant army stops mattering.
 *
 * At 0.35 an enemy four squares off counts for about two fifths of what it
 * would beside you, and one twelve squares away for a fifth. Tuned so that two
 * armies at opposite ends of a twenty-square board read as *nearly no*
 * pressure — which is the case this exists for.
 */

/**
 * The one foundry this side is concentrating production at, or -1.
 *
 * **The scattering problem.** Routing sends every unit to the *nearest* valid
 * machine, which is the right rule for a rule — it needs no plan and it cannot
 * deadlock — and it means six units split two apiece across three foundries.
 * A knight costs six. Nobody ever mints, and every foundry sits at two thirds
 * of a knight forever, which reads as an economy that works and produces
 * nothing.
 *
 * Nothing in `src/engine/rules/` is going to fix that: routing must not need a plan.
 * What a *player* does about it is pick a factory and aim their lanes at it,
 * and that is exactly what this is — an opinion the planner holds, expressed
 * through the ordinary commands, the same way a person would express it.
 *
 * Concentration begets concentration: the foundry that already holds the most
 * wins ties, so the choice is stable across turns without needing to be
 * remembered. A hub that moved every time a pawn dropped a bar somewhere would
 * scatter material exactly as thoroughly as having no hub at all.
 */
export function productionHub(state: GameState, side: Color, w: Weights = WEIGHTS): number {
  let best = -1;
  let bestScore = -Infinity;

  for (const [sq, piece] of state.position.board) {
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    if (!isFoundry(machineType(state.position, sq))) continue;

    // What it is already holding, which is what makes the choice sticky, and
    // how well placed it is for everything else we own.
    let score = bufferTotal(state.industry, sq, "in") * w.hubStock;
    for (const [other, mine] of state.position.board) {
      if (areEnemies(state.position, side, pieceColor(mine))) continue;
      score -= Math.max(
        Math.abs(fileOf(other) - fileOf(sq)),
        Math.abs(rankOf(other) - rankOf(sq))
      );
    }
    // Ties by square, never by Map order: two clients would agree only by luck
    // and a hub that flickered would scatter material as thoroughly as none.
    if (score > bestScore || (score === bestScore && sq < best)) {
      bestScore = score;
      best = sq;
    }
  }
  return best;
}

/**
 * How much a unit already banked at a foundry counts toward keeping it the hub.
 *
 * Well above a square of distance, because the cost of changing your mind is
 * the whole stock you have accumulated somewhere else — and the failure this
 * exists to prevent is material spread thin, which is what a hub that moves
 * produces.
 */

/** Somewhere this piece could put material down. For the shelf and the tests. */
export const strandedAt = (state: GameState, sq: number, side: Color): number =>
  bufferTotal(state.industry, sq, "in") * (1 - reachFactor(sq, consumerSquares(state, side)));

/**
 * The highest grade this side's chain can *reach*, however much ore it digs.
 *
 * A refinery may never process the same unit twice, so a bar's grade is the
 * number of **distinct ovens** it has been through — and `MINT_GRADE` prices a
 * rook at grade 2 and a queen at grade 3. One oven is therefore a permanent
 * ceiling of grade 1: knights and bishops, forever, no matter how well the
 * factory runs.
 *
 * Which makes an oven a *tier unlock* rather than a rate. That is the thing a
 * flat "more machines is better" term cannot express and the reason it is
 * worth its own function: the second bishop is what makes a rook exist at all,
 * and the third is what makes a queen exist at all. Nothing else on the board
 * does that.
 *
 * A king counts. It is a refinery, it cannot be captured, and it is why every
 * side has a ceiling of at least one.
 *
 * **Metal already in hand raises it, and leaving that out was a bug.** Ovens
 * answer "what could this chain *make*", which is not the same question as
 * "what can this side *spend*": material arrives by other doors — a captured
 * piece spills its cargo, a level seeds refined metal, a lane delivers from an
 * ally, an oven is taken after it has already cooked. A side holding thirty-two
 * bars of diamond behind a one-oven ceiling had rook and queen struck off the
 * list *by construction*, so `bestRecipe` could only choose among pawn, knight
 * and bishop — and with the army already crowded it saved for a **pawn** while
 * sitting on a queen's worth of diamond. Reported exactly that way.
 *
 * Any single unit at a grade is enough to unlock its tier. It does not need to
 * be the whole recipe: `waitFor` already prices the shortfall honestly, and a
 * hard "can you pay for all of it" test here would be the affordability gate
 * that `bestRecipe` deliberately replaced with a discount.
 */
export function mintCeiling(state: GameState, side: Color): number {
  let ovens = 0;
  for (const [sq, piece] of state.position.board) {
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    if (refinesAt(state.position, sq)) ovens++;
  }
  // **An oven with no coal anywhere raises no metal** (§31 M2): its rung costs
  // a fuel point, a point is a burnt coal, so with none to be had the chain
  // makes nothing above iron and only what is banked counts.
  if (DESIGN.metalNeedsFuel && ovens > 0 && !coalInReach(state, side)) ovens = 0;
  const top = topMetal();
  let reach = Math.min(top, ovens);
  // Only the grades the ovens cannot already account for, so the common case
  // — a chain whose ovens are its ceiling — costs one comparison and no sweep.
  for (let grade = top; grade > reach; grade--) {
    if (bankedAtGrade(state, side, grade) > 0) {
      reach = grade;
      break;
    }
  }
  return reach;
}

/**
 * Whether this side can come by any coal at all (§31 M2): a point already made,
 * coal in its own hands, lanes or buildings, or any loose or seamed in the
 * ground. Not *how much* — `mintCeiling` asks whether a rung is reachable, and
 * the routing carries what there is. Cheapest stores first, out at the first.
 */
export function coalInReach(state: GameState, side: Color): boolean {
  const { industry, position } = state;
  for (const [sq, points] of industry.fuel) {
    if (points > 0 && !areEnemies(position, side, pieceColor(pieceAt(position, sq)))) return true;
  }
  for (const [, units] of industry.dropped) if (units.some(burnsAsCoal)) return true;
  for (const [sq] of industry.deposit) if (burnsAsCoal(depositYieldAt(industry, sq))) return true;
  for (const [sq, piece] of position.board) {
    if (areEnemies(position, side, pieceColor(piece))) continue;
    for (const bank of ["in", "out"] as const) {
      for (let i = 0; i < unitCount(industry, sq, bank); i++) {
        if (burnsAsCoal(unitAt(industry, sq, i, bank))) return true;
      }
    }
  }
  for (const [sq, units] of industry.beltCargo) {
    if (areEnemies(position, side, beltOwnerAt(industry, sq))) continue;
    if (units.some(burnsAsCoal)) return true;
  }
  for (const [, structure] of industry.structures) {
    if (areEnemies(position, side, structure.owner)) continue;
    if (structure.stock.some(burnsAsCoal)) return true;
  }
  return false;
}

/**
 * What one more of this piece would be worth to the *chain*, on top of what it
 * is worth in a trade.
 *
 * Chess values a knight and a bishop the same, and chess is right: in a trade
 * they are the same piece. They are not remotely the same *purchase*. A bishop
 * is an oven — it raises what the whole factory can ever produce — and a knight
 * is a courier with a small bag and no machine role at all. Scoring a mint by
 * `PIECE_VALUE` alone made those two identical, and `MINTABLE` lists the knight
 * first, so every tie went to the knight and the opponent built nothing else.
 *
 * Priced against what the chain is short of, never as a flat bonus, for the
 * same reason `capacityValue` is: a term that always says "build another" is a
 * term an opponent follows until it has no army left.
 */
export function roleValue(
  state: GameState,
  side: Color,
  type: number,
  /** Where it would be put down, when that is known. Only a pawn cares. */
  at?: number,
  w: Weights = WEIGHTS
): number {
  return pieceNeeds(state, side, at, w)[type as PieceType] ?? 0;
}

/**
 * How badly this army wants each piece, right now.
 *
 * **One reading per piece, and each of them is about a different thing.** That
 * is the whole point of a table rather than a single "is the factory doing
 * well" number: an army short of refinement and an army short of somewhere to
 * put its ore have completely different answers, and a scalar cannot tell them
 * apart. Every row is derived from the board, so the table changes turn by turn
 * and nothing here is a schedule.
 *
 * In centipawns, so it adds to `PIECE_VALUE` and both halves of a mint —
 * "what is this worth in a fight" and "what is it worth to my factory" — argue
 * on one scale.
 */
export function pieceNeeds(
  state: GameState,
  side: Color,
  at?: number,
  w: Weights = WEIGHTS
): Record<PieceType, number> {
  const view = viewFactory(state, side, w);
  const counts = machineCounts(state, side);
  const reach = scrapByReach(state, side, w);

  return {
    // ---- Refinement. --------------------------------------------------
    //
    // A bishop is an oven, and since a refinery may not process the same unit
    // twice, the ovens you own are the highest grade you can ever reach. So
    // the biggest number in the table is the **tier unlock**: going from one
    // oven to two turns a rook from a piece this chain can never make into one
    // it can.
    //
    // Scaled by how much of the way there the side already is. A tier you hold
    // no material for is not unlocked, it is merely permitted — paid in full
    // it made a second oven the answer to every question on an empty board.
    [BISHOP]: unlockValue(state, side, counts) + shortOf(counts.raw, counts.refineries, 4, w.capacity),

    // ---- Somewhere to spend. ------------------------------------------
    //
    // A rook is a foundry: a second place that can turn metal into pieces.
    // Measured against **everything the side is holding**, not only the
    // refined part, because a hoard is a hoard whatever grade it is in — and
    // "lots of material and one place to spend it" is exactly the shape of a
    // factory that produces nothing.
    // `material + banked`: the reading is *everything the side is holding*,
    // and the two were one field until banked metal was split out. Dropping
    // either half here would ask for a second foundry on the strength of what
    // is riding the lanes, or on the strength of what is sitting in hands, and
    // never both.
    [ROOK]: shortOf(
      (view.material + view.banked) / w.grade[1],
      counts.foundries,
      8,
      w.capacity
    ),

    // ---- Collection. --------------------------------------------------
    //
    // The queen sweeps every square she crosses, which makes her the answer to
    // material that is *scattered* rather than merely plentiful. Scrap sitting
    // under a harvester is already being dealt with; scrap nobody is near, in
    // several places, is a walk no pawn is going to make.
    [QUEEN]: reach.far * w.grade[0] * w.sweepShare,

    // ---- Logistics, and aim. ------------------------------------------
    //
    // The knight is the one piece with no machine of its own, and it has two
    // jobs because of it. It is a courier — worth something when material is
    // stranded far from anything that could use it — and it is the **only**
    // thing that re-aims a pawn. A king points the pawns it mints away from
    // itself, so an army that has been forging a screen accumulates pawns
    // pointing in four directions, and nothing else on the board can fix them.
    [KNIGHT]:
      Math.max(0, view.material - view.inFlight) * w.carryShare +
      misaimedPawns(state, side) * w.reaimValue,

    // ---- Extraction. --------------------------------------------------
    //
    // The one thing that puts ore into the system at all, and the cheapest
    // piece on the board. Measured as the **backlog**: how many turns of
    // digging are stacked up on the floor, at the rates the side actually digs
    // at. The bottleneck reading the other rows use saturates at one heap's
    // worth, which made fifty units lying unworked look exactly like six.
    //
    // And when the square it would stand on is known, that decides it: a pawn
    // put down where there is nothing to dig and nowhere to walk is a hundred
    // centipawns of material spent on an obstruction.
    [PAWN]:
      Math.min(reach.near / Math.max(1, counts.digRate) / w.diggingBacklog, 1) *
      w.capacity *
      w.sourceUrgency *
      (at === undefined ? 1 : usefulSpot(state, side, at, w)),

    // A king is never minted; the row exists so the table covers the type.
    [KING]: 0
  };
}

/** What a second oven is worth, given how close this side is to using one. */
function unlockValue(
  state: GameState,
  side: Color,
  counts: MachineCounts
): number {
  const top = topMetal();
  const before = Math.min(top, counts.refineries);
  const after = Math.min(top, counts.refineries + 1);
  if (after <= before) return 0;
  // An oven with no coal to burn unlocks nothing on the metal line (§31 M2).
  if (DESIGN.metalNeedsFuel && !coalInReach(state, side)) return 0;
  return (tierValue(after) - tierValue(before)) * towardTier(state, side, before);
}

/**
 * Scrap this side could walk to, and scrap it could not.
 *
 * **The split is what separates the digger's row from the queen's**, and
 * without it they are the same reading and the cheap piece wins every time. A
 * heap two squares from a harvester is a digger's job: send one, or send the
 * one already standing there. Heaps scattered across eleven squares of open
 * board are not — no pawn is making that walk — and the queen is the only
 * piece whose *path* collects, so ground nobody can reach is precisely hers.
 *
 * Measured from the nearest harvester rather than from the nearest piece: it
 * is the walk a *digger* has to make that decides whether digging is the
 * answer.
 */
function scrapByReach(
  state: GameState,
  side: Color,
  w: Weights = WEIGHTS
): { near: number; far: number } {
  const diggers: number[] = [];
  const anyone: number[] = [];
  for (const [sq, piece] of state.position.board) {
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    anyone.push(sq);
    if (isHarvesterAt(state.position, sq)) diggers.push(sq);
  }

  // **With no digger at all, measure from the army instead.**
  //
  // "Near" means "a digger could walk to it", and with an empty digger list
  // every heap on the board came out `far` — the queen's row, never the
  // pawn's. So a side that had lost its last pawn wanted no pawns, which is a
  // deadlock: extraction is the one thing that cannot bootstrap itself.
  //
  // It became reachable the moment the king stopped digging, because a run
  // opens with a king and nothing else — the first thing the table is ever
  // asked is "what should this army build", and the answer has to be a digger.
  const from = diggers.length > 0 ? diggers : anyone;

  let near = 0;
  let far = 0;
  for (const sq of mineableSquares(state.industry)) {
    const amount = mineableAt(state.industry, sq);
    if (amount <= 0) continue;
    let nearest = Infinity;
    for (const at of from) {
      nearest = Math.min(
        nearest,
        Math.max(Math.abs(fileOf(at) - fileOf(sq)), Math.abs(rankOf(at) - rankOf(sq)))
      );
    }
    if (nearest <= w.sweepReach) near += amount;
    else far += amount;
  }
  return { near, far: Math.min(far, w.sweepCap) };
}

/** How far off scrap has to be before walking to it stops being a pawn's job. */

/** Past this the queen is already fully justified; more scrap changes nothing. */

/**
 * Pawns of this side pointing somewhere other than the way its army marches.
 *
 * A king points every pawn it mints *away from himself*, which is right for a
 * screen and wrong for everything afterwards — so an army that has been
 * forging accumulates pawns facing four directions, and the knight's re-aim is
 * the only thing on the board that can fix one.
 */
function misaimedPawns(state: GameState, side: Color): number {
  const forward = forwardFacing(side);
  let wrong = 0;
  for (const [sq, piece] of state.position.board) {
    if (pieceColor(piece) !== side || pieceType(piece) !== PAWN) continue;
    if (facingAt(state.position, sq) !== forward) wrong++;
  }
  return wrong;
}

/** What turning one pawn back around is worth, as a share of a knight's turn. */

/**
 * How much of a pawn's worth survives being put down *here*.
 *
 * A minted piece lands on the first free square beside the foundry, and that
 * square is not a choice the planner gets to make — so the honest question is
 * whether it is anywhere worth standing. A pawn on scrap is working the turn
 * it appears; one that can walk to scrap is working shortly; one with nothing
 * within reach and nowhere to walk is a hundred centipawns of material spent
 * on an obstruction.
 */
function usefulSpot(state: GameState, side: Color, at: number, w: Weights = WEIGHTS): number {
  if (at < 0) return 0;
  if (mineableAt(state.industry, at) > 0) return 1;

  let nearest = Infinity;
  for (const sq of mineableSquares(state.industry)) {
    if (mineableAt(state.industry, sq) <= 0) continue;
    nearest = Math.min(
      nearest,
      Math.max(Math.abs(fileOf(at) - fileOf(sq)), Math.abs(rankOf(at) - rankOf(sq)))
    );
  }
  if (!Number.isFinite(nearest)) return w.spotFloor;
  // Room to walk matters as much as somewhere to walk to: a pawn wedged
  // against its own army reaches nothing however close the heap is.
  const room = freeAround(state, at) === 0 ? w.spotFloor : 1;
  return Math.max(w.spotFloor, 1 - nearest / w.sweepReach) * room;
}

/** Empty squares touching this one. */
function freeAround(state: GameState, at: number): number {
  let free = 0;
  for (let df = -1; df <= 1; df++) {
    for (let dr = -1; dr <= 1; dr++) {
      if (df === 0 && dr === 0) continue;
      const sq = square(fileOf(at) + df, rankOf(at) + dr);
      if (state.position.cells.has(sq) && !state.position.board.get(sq)) free++;
    }
  }
  return free;
}

/**
 * What a pawn is worth industrially in the worst spot on the board.
 *
 * Not zero: it is still a body, it can still be walked somewhere over several
 * turns, and a term that reaches zero would make one bad square veto a mint
 * the rest of the position wants.
 */

/**
 * How much a mint has to be worth before it is worth making at all.
 *
 * **Sometimes an army does not want another piece.** It has more than it can
 * usefully move, there is plenty on the floor to dig, and nobody is coming —
 * and in that position the material is better left in the bank and the turn
 * better spent working what is already there. Nothing else in the table can
 * say that, because every row is a reason *for* something.
 *
 * Three readings, all pointing the same way:
 *
 *  - **How big the army already is.** Every extra piece is another thing
 *    competing for the same squares and the same lanes.
 *  - **How much digging is waiting.** Turns spent forging are turns not spent
 *    working a graveyard that is already open.
 *  - **How hard it is being leaned on**, which cancels both. An army under
 *    threat wants bodies now, whatever the factory would prefer.
 */
export function mintRestraint(state: GameState, side: Color, w: Weights = WEIGHTS): number {
  const counts = machineCounts(state, side);
  let army = 0;
  for (const [, piece] of state.position.board) {
    if (!areEnemies(state.position, side, pieceColor(piece))) army++;
  }

  const crowd = Math.min(army / w.crowdedArmy, 1);
  const digging = Math.min(counts.scrap / Math.max(1, counts.digRate) / w.diggingBacklog, 1);
  return w.restraintValue * crowd * digging * (1 - pressure(state, side, w));
}

/** An army this size is as much as anyone wants to be moving one piece a turn. */

/**
 * The most a quiet, crowded, busy army will hold out for.
 *
 * About a knight: enough that a marginal piece is refused and not so much that
 * a genuine bottleneck — an army with no oven, a graveyard with no diggers —
 * ever loses to it.
 */

/**
 * The best piece a chain with this many ovens could ever mint, by grade.
 *
 * Derived from the recipes rather than typed out, or a tuning change to the
 * bills would silently leave the opponent valuing an unlock it no longer
 * gets.
 */
function tierValue(ceiling: number): number {
  let best = 0;
  for (const type of PLANNER_MINTS) {
    if (mintGradeOf(type) <= ceiling) best = Math.max(best, PIECE_VALUE[type] * 100);
  }
  return best;
}

/** How much of the work at a stage is going unserved, in `capacity`. */
const shortOf = (work: number, machines: number, per: number, capacity: number): number =>
  Math.max(0, Math.min(work / per, 1) - Math.min(machines / 2, 1)) * capacity;

/**
 * How much of the way this side is toward actually spending the next tier up.
 *
 * The material it holds at its *current* ceiling, against what the cheapest
 * piece at the next one costs. Nothing banked means a second oven unlocks a
 * tier the chain has no material for, which is not an unlock — and pricing it
 * as one made a bishop the answer to every question on an untouched board.
 */
function towardTier(
  state: GameState,
  side: Color,
  ceiling: number
): number {
  let cheapest = Infinity;
  for (const type of PLANNER_MINTS) {
    // `mintCost`, never `PIECE_VALUE * MINT_COST_MULTIPLIER`. They agreed until
    // the knight and the bishop were discounted, and a second copy of a price
    // is a place for the planner to save up for a number a foundry does not
    // charge.
    if (mintGradeOf(type) === ceiling + 1) cheapest = Math.min(cheapest, mintCost(type));
  }
  if (!Number.isFinite(cheapest) || cheapest <= 0) return 0;
  return Math.min(1, bankedAtGrade(state, side, ceiling) / cheapest);
}

/** Turns of unworked scrap at which another digger is fully justified. */

/**
 * How much more the *source* of the chain is worth than a stage in the middle.
 *
 * Half again. Nothing downstream runs at all without extraction — an oven with
 * nothing to cook and a foundry with nothing to spend are both waiting on the
 * same thing — so the stage that feeds everything is worth more than the
 * bottleneck reading alone would say.
 */

/** What a queen's sweep is worth per unit of loose scrap she could collect. */

/** What a courier is worth per point of material that is not on its way anywhere. */

interface MachineCounts {
  harvesters: number;
  /**
   * Units a turn this side could dig, at its actual rates.
   *
   * Not the harvester *count*: a king digs three times a pawn's rate, so
   * counting heads makes a king-and-pawn factory look half as fast as it is
   * and asks for diggers it does not need.
   */
  digRate: number;
  refineries: number;
  foundries: number;
  scrap: number;
  raw: number;
  refined: number;
}

/** The stages of the chain and the work waiting at each, counted once. */
function machineCounts(state: GameState, side: Color): MachineCounts {
  const counts: MachineCounts = {
    harvesters: 0,
    digRate: 0,
    refineries: 0,
    foundries: 0,
    scrap: 0,
    raw: 0,
    refined: 0
  };

  for (const [sq, piece] of state.position.board) {
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    const kind = machineType(state.position, sq);
    if (isHarvesterAt(state.position, sq)) {
      counts.harvesters++;
      counts.digRate += harvestRate(state.position, sq);
    }
    if (refinesAt(state.position, sq)) counts.refineries++;
    if (isFoundry(kind)) counts.foundries++;

    for (const store of ["in", "out"] as const) {
      for (let i = 0; i < unitCount(state.industry, sq, store); i++) {
        if (unitGrade(unitAt(state.industry, sq, i, store)) === 0) counts.raw++;
        else counts.refined++;
      }
    }
  }
  for (const sq of mineableSquares(state.industry)) {
    counts.scrap += mineableAt(state.industry, sq);
  }
  return counts;
}

/**
 * Units this side holds at `grade` or better, anywhere at all.
 *
 * Anywhere, deliberately: a foundry's own stock is what it can *spend*, and
 * this is what it can spend *eventually* — which is the question a recipe
 * asks, since a recipe is a thing you save toward. Asking only about the
 * foundry meant a side with fourteen bars of steel spread across its pieces
 * could never aim at the rook they add up to.
 */
export function bankedAtGrade(state: GameState, side: Color, grade: number): number {
  let total = 0;
  // The metal line only: this is the ceiling-and-shortfall arithmetic behind
  // what a foundry can mint, and every recipe's gating row is metal.
  const count = (unit: number): void => {
    if (unitMeets(unit, METAL, grade)) total++;
  };

  for (const [sq, piece] of state.position.board) {
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    for (const store of ["in", "out"] as const) {
      for (let i = 0; i < unitCount(state.industry, sq, store); i++) {
        count(unitAt(state.industry, sq, i, store));
      }
    }
    for (let i = 0; i < cookingCount(state.industry, sq); i++) {
      count(cookingUnitAt(state.industry, sq, i));
    }
  }
  for (const [sq, units] of state.industry.beltCargo) {
    if (areEnemies(state.position, side, beltOwnerAt(state.industry, sq))) continue;
    for (const unit of units) count(unit);
  }
  for (const [, structure] of state.industry.structures) {
    if (areEnemies(state.position, side, structure.owner)) continue;
    for (const unit of structure.stock) count(unit);
  }
  return total;
}

/**
 * How spread out a side's army is: mean Chebyshev distance from its own centre.
 *
 * **Chebyshev, because that is the board's own metric** — it is what territory,
 * reach and every distance term in this file already measure in, and a
 * Euclidean answer here would be the one number in the planner that disagrees
 * with the rest about what "far" means.
 *
 * From the centroid rather than pairwise: pairwise is O(n squared) over an army
 * that can be ninety pieces, and this is asked once per evaluated board.
 */
export function armySpread(state: GameState, side: Color): number {
  let count = 0;
  let file = 0;
  let rank = 0;
  for (const [sq, piece] of state.position.board) {
    if (pieceColor(piece) !== side) continue;
    count++;
    file += fileOf(sq);
    rank += rankOf(sq);
  }
  if (count < 2) return 0;
  const midFile = file / count;
  const midRank = rank / count;
  let total = 0;
  for (const [sq, piece] of state.position.board) {
    if (pieceColor(piece) !== side) continue;
    total += Math.max(Math.abs(fileOf(sq) - midFile), Math.abs(rankOf(sq) - midRank));
  }
  return total / count;
}
